<template>
  <div v-if="isDisplay" class="workload-container">
    <b-card>
      <div class="organism-workload_header card-padding flex-wrap">
        <span class="header__label">Workload</span>
      </div>
      <div class="filter-container card-padding pt-0">
        <div class="row mt-0">
          <div
            v-if="hasWorkloadAllUsersPermission"
            class="col-12 col-md-6 col-lg-4"
          >
            <atom-select
              v-model="filterList.user"
              title="User"
              placeholder="Select User"
              label="name"
              :option="userOption"
            />
          </div>
          <div
            class="col-12 mt-1 mt-md-0"
            :class="[
              hasWorkloadAllUsersPermission
                ? 'col-md-6 col-lg-4'
                : 'col-lg-6'
            ]"
          >
            <atom-select
              v-model="filterList.action"
              title="Action"
              placeholder="Select Action"
              label="name"
              :option="actionTypeOptions"
            />
          </div>
          <div
            class="col-12 mt-1 mt-lg-0"
            :class="[
              hasWorkloadAllUsersPermission
                ? 'col-md-6 col-lg-4'
                : 'col-lg-6'
            ]"
          >
            <atom-select
              v-model="filterList.resource"
              title="Resource"
              placeholder="Select Resource"
              label="name"
              :option="resourceTypeOptions"
            />
          </div>
        </div>

        <div class="row mt-1">
          <div class="col-12 col-lg-3">
            <atom-input
              v-model="filterList.title"
              title="Title"
              placeholder="Title"
            />
          </div>
          <div class="col-12 col-md-6 col-lg-3 w-100 mt-1 mt-lg-0">
            <div>
              <atom-datepicker
                datepickerId="workload-datepicker-start"
                v-model="filterList.dateTime.start"
                title="Start Date"
                type="dateNtime"
                placeholder="Select Start Date"
                :maxDate="
                  filterList.dateTime.end
                    ? filterList.dateTime.end
                    : filterList.dateTime.now
                "
                :isDisabled="!filterList.isEnableDate"
              ></atom-datepicker>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-3 w-100 mt-1 mt-lg-0">
            <div>
              <atom-datepicker
                datepickerId="workload-datepicker-end"
                v-model="filterList.dateTime.end"
                title="End Date"
                type="dateNtime"
                placeholder="Select End Date"
                :minDate="filterList.dateTime.start"
                :maxDate="filterList.dateTime.end"
                :isDisabled="!filterList.isEnableDate"
              ></atom-datepicker>
            </div>
          </div>
          <div
            class="col-12 col-lg-3 d-flex mt-1 mt-lg-0 justify-content-end justify-content-lg-start"
          >
            <div class="d-flex flex-column">
              <label>Filter Date?</label>
              <atom-switch
                class="filter__switch"
                v-model="filterList.isEnableDate"
              ></atom-switch>
            </div>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                class="search__button ml-2"
                @click="clickButtonSearch"
              ></atom-button>
              <div
                class="d-flex reset-filter-button ml-1"
                @click="onSetDefaultFilterData"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <div class="d-flex flex-column gap-14px mt-1">
      <template v-if="workloadList.length">
        <div v-for="workload in workloadList" :key="workload.id">
          <molecule-workload-item :data="workload" />
        </div>
      </template>
      <template v-else>
        <b-card
          class="card-body d-flex flex-column align-items-center p-2"
        >
          <feather-icon
            icon="CloudOffIcon"
            class="empty-data__icon"
          ></feather-icon>
          <div class="empty-data__text--title">No Data</div>
        </b-card>
      </template>
    </div>

    <b-card class="mt-1">
      <molecule-pagination-with-per-page
        :page="filterList.currentPage"
        :total-count="filterList.totalCount"
        :per-page="filterList.perPage"
        @onUpdatePaginationOrPerPage="onChangePagination"
      ></molecule-pagination-with-per-page>
    </b-card>
  </div>
</template>

<script>
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomSwitch from "@/components/atoms/AtomSwitch.vue"
import MoleculeWorkloadItem from "@/components/molecules/portal/workload/MoleculeWorkloadItem.vue"
import MoleculePaginationWithPerPage from "@/components/molecules/MoleculePaginationWithPerPage.vue"
import { BCard } from "bootstrap-vue"

import { mapGetters } from "vuex"

import {
  ResourceTypeOptions,
  ActionTypeOptions
} from "@/constants/workload"
import { PermissionUser } from "@/constants/permission-user"

import mixins from "@/mixins"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsUser from "@/mixins/mixinsUser"

import { getWorkloadList, getAllUserList } from "@/api/api-module1"

export default {
  name: "OrganismWorkload",
  components: {
    AtomButton,
    AtomDatepicker,
    AtomInput,
    AtomSelect,
    AtomSwitch,
    BCard,
    MoleculeWorkloadItem,
    MoleculePaginationWithPerPage
  },
  mixins: [
    mixins,
    mixinsDatetime,
    mixinsSiteHelper,
    mixinsObjectHelper,
    mixinsUser
  ],
  data() {
    return {
      filterList: {},
      resourceTypeOptions: ResourceTypeOptions,
      actionTypeOptions: ActionTypeOptions,
      workloadList: [],
      isDisplay: false,

      userOption: []
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getInternalWorkloadFilter"]),

    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),

    hasWorkloadAllUsersPermission() {
      return this.hasPermission(PermissionUser.WPM.WORKLOAD_ALL_USERS_READ)
    }
  },
  watch: {
    async siteSetting() {
      if (!this.isRouterInTransition) {
        await this.getWorkloadDataList()
      }
    }
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      this.showLoading(true)
      try {
        await this.getUserOption()
        this.cloneFilterDataFromVuex()
        await this.onSetDefaultFilterData()
        this.reBuild()
      } finally {
        this.showLoading(false)
      }
    },

    reBuild() {
      this.isDisplay = false
      this.$nextTick(() => {
        this.isDisplay = true
      })
    },

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getInternalWorkloadFilter
      this.filterList = this.deepCopyObject(filterDataFromVuex)
    },

    async getWorkloadDataList() {
      this.showLoading(true)
      const filterData = await this.filterDecoration()
      await getWorkloadList(filterData).then((response) => {
        this.filterList.totalCount = response.totalCount
        this.workloadList = response.data.map((item) => ({
          id: item.id,
          action: item.action,
          dateTime: item.createdAt,
          resource: {
            type: item.resourceType,
            title: item.title,
            id: item.resourceId
          },
          site: item.siteName,
          name: item.createdBy,
          username: item.username || "Unknown User",
          items: item.changeDetails.map((change) => ({
            key: change.key,
            oldValue: change.oldValue,
            newValue: change.newValue,
            isUpdated: change.isChanged
          })),
          isDeleted: item.isDeleted
        }))
      })
      this.showLoading(false)
    },

    async onSetDefaultFilterData() {
      this.cloneFilterDataFromVuex()
      await this.getWorkloadDataList()
    },

    async clickButtonSearch() {
      await this.getWorkloadDataList()
    },

    async filterDecoration() {
      const filter = {
        username: this.hasWorkloadAllUsersPermission
          ? this.filterList?.user?.value || undefined
          : undefined,
        action: this.filterList?.action?.value || undefined,
        resourceType: this.filterList?.resource?.value || undefined,
        title: this.filterList?.title || undefined,
        startDate: this.filterList?.isEnableDate
          ? this.removeTimeZone(this.filterList?.dateTime?.start)
          : undefined,
        endDate: this.filterList.isEnableDate
          ? this.removeTimeZone(this.filterList?.dateTime?.end)
          : undefined,
        siteIds: this.siteOptions.map((site) => site.value),
        pageSize: this.filterList?.perPage,
        pageNumber: this.filterList?.currentPage
      }
      return filter
    },

    async getUserOption() {
      this.showLoading(true)
      const params = {
        siteIds: this.siteOptions.map((site) => site.value)
      }
      await getAllUserList(params)
        .then((response) => {
          this.userOption = response.map((user) => ({
            value: user.username,
            name: `${user.firstName} ${user.lastName}`
          }))
        })
        .finally(() => this.showLoading(false))
    },

    async onChangePagination(currentPerPage, currentPage) {
      this.filterList.currentPage = currentPage
      this.filterList.perPage = currentPerPage
      await this.getWorkloadDataList()
    }
  }
}
</script>

<style lang="scss" scoped>
.workload-container {
  padding-bottom: 2rem;
}

.empty-data__icon {
  width: 50px;
  height: 50px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 14px;
  margin-top: 20px;
}

.organism-workload_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.card-padding {
  padding: 1.5rem;
}

.card {
  margin-bottom: 0 !important;
}

.card-body {
  padding: 0;
}

.filter-container {
  border-bottom: 1px solid #e9ecef;
}

.filter__switch {
  padding-top: 10px;
  height: fit-content;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    color: #7367f0;
  }
}
</style>
